import { AIRFINITY_VIEWER_PARTS, DIFFUSER_VIEWER_CONFIG, ProjectList } from './ProjectList';

// Ordered category keys — display titles live in translations[lang].skillsPage.categories
export const SKILL_CATEGORIES = [
  'software',
  'ai',
  'mobile',
  'embedded',
  'electronics',
  'cad',
  'robotics',
  'devops',
];

// The mechatronics story: four top-level disciplines. Categories stay the
// fine-grained grouping; every category rolls up into exactly one pillar.
// Display names/taglines live in translations[lang].skillsPage.pillars.
export const PILLARS = ['mechanical', 'electrical', 'programming', 'ai'];

export const CATEGORY_PILLAR = {
  cad: 'mechanical',
  robotics: 'mechanical',
  electronics: 'electrical',
  embedded: 'electrical',
  software: 'programming',
  mobile: 'programming',
  devops: 'programming',
  ai: 'ai',
};

export function pillarOf(category) {
  return CATEGORY_PILLAR[category] || null;
}

// Canonical skill registry. `name` (or an alias) must match the chip text used in
// ProjectList `technologies` exactly (case-insensitive). The /skills page derives
// each skill's project list from ProjectList, so a skill only renders when at
// least one project backs it — no unverifiable claims.
export const SKILLS = [
  // Software & Web
  { name: 'TypeScript', category: 'software' },
  { name: 'React', category: 'software' },
  { name: 'Next.js', category: 'software' },
  { name: 'Node.js', category: 'software' },
  { name: 'Express', category: 'software' },
  { name: 'Python', category: 'software' },
  { name: 'Visual Basic', category: 'software' },
  { name: 'Scratch', category: 'software' },
  { name: 'PostgreSQL', category: 'software' },
  { name: 'Prisma', category: 'software' },
  { name: 'Vite', category: 'software' },
  { name: 'Headless Chromium', category: 'software' },
  { name: 'jsPDF', category: 'software' },
  { name: 'Material UI', category: 'software' },
  { name: 'Baileys', category: 'software' },
  { name: 'Discord Bot Development', category: 'software' },
  { name: 'Chrome Extensions (MV3)', category: 'software' },
  { name: 'Database Design', category: 'software' },
  { name: 'UI/UX Design', category: 'software' },
  { name: 'Transport Systems Design', category: 'software' },

  // AI & Data
  { name: 'LLM Orchestration', category: 'ai' },
  { name: 'RAG Pipelines', category: 'ai' },
  { name: 'Google Gemini', category: 'ai' },
  { name: 'pgvector', category: 'ai' },

  // Mobile & Cross-Platform
  { name: 'Flutter', category: 'mobile' },
  { name: 'Dart', category: 'mobile' },
  { name: 'Firebase', category: 'mobile' },
  { name: 'Cloud Functions', category: 'mobile' },
  { name: 'Riverpod', category: 'mobile' },
  { name: 'GetX', category: 'mobile' },
  { name: 'GoRouter', category: 'mobile' },
  { name: 'Drift', category: 'mobile' },
  { name: 'Adapty', category: 'mobile' },
  { name: 'AdMob', category: 'mobile' },
  { name: 'MIT App Inventor', category: 'mobile' },

  // Embedded & IoT
  { name: 'Raspberry Pi', category: 'embedded' },
  { name: 'Arduino', category: 'embedded' },
  { name: 'ESP32', category: 'embedded' },
  { name: 'ESP8266', category: 'embedded' },
  { name: 'Modbus RTU / RS-485', category: 'embedded', aliases: ['Modbus'] },
  { name: 'MQTT', category: 'embedded' },
  { name: 'Embedded Linux (Buildroot)', category: 'embedded', aliases: ['Embedded Linux', 'Buildroot'] },
  { name: 'Bluetooth', category: 'embedded' },
  { name: 'Sensor Integration', category: 'embedded' },
  { name: 'PID Control', category: 'embedded' },
  { name: 'Klipper', category: 'embedded' },
  { name: 'Home Automation', category: 'embedded' },
  { name: 'Firmware Configuration', category: 'embedded' },
  { name: 'Assistive Technology', category: 'embedded' },

  // Electronics & PCB
  { name: 'KiCad', category: 'electronics' },
  { name: 'Circuit Design', category: 'electronics' },
  { name: 'Analog Electronics', category: 'electronics' },
  { name: 'Electronics Prototyping', category: 'electronics' },
  { name: 'Electromagnetics', category: 'electronics' },
  { name: 'Soldering', category: 'electronics' },
  { name: 'Motor Drivers', category: 'electronics' },
  { name: 'Solar Power', category: 'electronics' },

  // 3D, CAD & Making
  { name: 'Fusion 360', category: 'cad' },
  { name: 'CAD Design', category: 'cad' },
  { name: '3D Printing', category: 'cad' },
  { name: '3D Printer Building', category: 'cad' },
  { name: 'Woodworking', category: 'cad' },
  { name: 'Prototype Fabrication', category: 'cad' },
  { name: 'Mechanical Design', category: 'cad' },
  { name: 'Closed-Loop Aquaponics', category: 'cad' },

  // Robotics & Automation
  { name: 'LEGO Mindstorms NXT', category: 'robotics' },
  { name: 'Robotic Arm Control', category: 'robotics' },
  { name: 'Mecanum Drive', category: 'robotics' },
  { name: 'FPV Drone Building', category: 'robotics' },
  { name: 'Motor Control', category: 'robotics' },
  { name: 'DC Motor Drive', category: 'robotics' },
  { name: 'Competition Robotics', category: 'robotics' },
  { name: 'Process Automation', category: 'robotics' },

  // Cloud & DevOps
  { name: 'Docker', category: 'devops' },
  { name: 'GitLab CI', category: 'devops' },
  { name: 'Linux VPS Operations', category: 'devops' },
  { name: 'S3 Object Storage', category: 'devops' },
  { name: 'WireGuard', category: 'devops' },
  { name: 'TrueNAS / ZFS', category: 'devops', aliases: ['TrueNAS', 'ZFS'] },
  { name: 'Self-Hosted GitLab', category: 'devops' },
  { name: 'Tailscale', category: 'devops' },
  { name: 'Cloudflare Tunnel', category: 'devops' },
  { name: 'Reverse Proxy & TLS', category: 'devops' },
  { name: 'DNS & Domain Management', category: 'devops' },
  { name: 'Web Security Hardening', category: 'devops' },
];

// URL identity for /skills/:slug detail pages
export function slugifySkill(name) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Interactive 3D evidence shown on a skill's detail page — a skill may carry
// several models. projectId names the owning project: tests enforce that the
// project lists this skill and already ships every referenced model in its own
// viewer3d — provenance, not just existence. captionKey selects the localized
// caption in translations[lang].skillsPage.showcaseCaptions.
export const SKILL_SHOWCASES = {
  'Fusion 360': [
    { projectId: 32, captionKey: 'airfinity-enclosure', parts: AIRFINITY_VIEWER_PARTS },
    { projectId: 39, captionKey: 'diffuser-assembly', ...DIFFUSER_VIEWER_CONFIG },
  ],
  KiCad: [
    // PCB only — untinted, so the viewer skips the enclosure raking light
    { projectId: 32, captionKey: 'airfinity-pcb', parts: [AIRFINITY_VIEWER_PARTS[0]] },
  ],
  'CAD Design': [
    { projectId: 39, captionKey: 'diffuser-assembly', ...DIFFUSER_VIEWER_CONFIG },
  ],
  'Mechanical Design': [
    { projectId: 39, captionKey: 'diffuser-assembly', ...DIFFUSER_VIEWER_CONFIG },
  ],
  '3D Printing': [
    // Enclosure only — the printed parts, without the fabricated PCB
    { projectId: 32, captionKey: 'printed-enclosure', parts: [AIRFINITY_VIEWER_PARTS[1], AIRFINITY_VIEWER_PARTS[2]] },
  ],
};

const chipIndex = new Map();
SKILLS.forEach((skill) => {
  [skill.name, ...(skill.aliases || [])].forEach((label) => {
    chipIndex.set(label.toLowerCase(), skill.name);
  });
});

// Chip text → canonical skill name, or null when unregistered
export function resolveSkillName(chip) {
  return chipIndex.get(chip.trim().toLowerCase()) || null;
}

// Registry joined against ProjectList: every skill with the projects that prove
// it (newest first) and the year span those projects cover.
export function skillsWithProjects() {
  const byName = new Map(
    SKILLS.map((skill) => [
      skill.name,
      {
        ...skill,
        slug: slugifySkill(skill.name),
        showcases: SKILL_SHOWCASES[skill.name] || [],
        projects: [],
      },
    ])
  );

  ProjectList.forEach((project) => {
    (project.technologies || []).forEach((chip) => {
      const skillName = resolveSkillName(chip);
      if (!skillName) return;
      const entry = byName.get(skillName);
      if (!entry.projects.some((p) => p.projectId === project.projectId)) {
        entry.projects.push({
          projectId: project.projectId,
          name: project.name,
          year: project.year,
          image: project.image,
          // 192px webp generated from the cover — tests verify it exists on disk
          thumb: project.image
            .replace('/assets/', '/assets/thumbs/')
            .replace(/\.(jpg|jpeg|png)$/i, '.webp'),
          tagline: project.tagline || null,
        });
      }
    });
  });

  const skills = [...byName.values()].filter((s) => s.projects.length > 0);
  skills.forEach((skill) => {
    skill.projects.sort((a, b) => b.year - a.year || a.name.localeCompare(b.name));
    // Discrete years, not a range — "2016, 2021" must not read as continuous use
    const years = [...new Set(skill.projects.map((p) => p.year))].sort((a, b) => a - b);
    skill.yearSpan =
      years.length <= 3 ? years.join(', ') : `${years[0]} – ${years[years.length - 1]}`;
  });
  return skills;
}

// Slug → joined skill entry, or null for unknown slugs
export function getSkillBySlug(slug) {
  return skillsWithProjects().find((s) => s.slug === slug) || null;
}
